import { AlertTriangle, Info as InfoIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cx } from './core/classes.js'

/** Inline callout for status messages, with an optional trailing action. */
export function Info(props: Info.Props) {
  const { action, className, message, type = 'info' } = props
  const Icon = type === 'info' ? InfoIcon : AlertTriangle
  return (
    <div
      className={cx(
        'flex items-center gap-2 rounded-body border px-3 py-2 label-12',
        styles[type],
        className,
      )}
    >
      <Icon className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1">{message}</span>
      {action && <Action action={action} />}
    </div>
  )
}

function Action(props: { action: Info.Action }) {
  const { action } = props
  const className =
    'shrink-0 cursor-pointer font-medium underline decoration-current/40 underline-offset-2 hover:decoration-current'
  if ('href' in action) {
    const external = action.external ?? true
    return (
      <a
        className={className}
        href={action.href}
        onClick={action.onClick}
        rel={external ? 'noreferrer noopener' : undefined}
        target={external ? '_blank' : undefined}
      >
        {action.label}
      </a>
    )
  }

  return (
    <button className={className} onClick={action.onClick} type="button">
      {action.label}
    </button>
  )
}

const styles = {
  error: 'border-negative bg-negative-subtle text-negative',
  info: 'border-info bg-info-subtle text-info',
  warning: 'border-warning bg-warning-subtle text-warning',
} as const

export declare namespace Info {
  /** Visual variant for an info callout. */
  type Type = keyof typeof styles

  /** Optional trailing action rendered inline on an info callout. */
  type Action =
    | {
        /** Whether the link opens in a new tab. @default true */
        external?: boolean | undefined
        /** Link target. */
        href: string
        /** Link text. */
        label: ReactNode
        /** Called when the link is clicked. */
        onClick?: (() => void) | undefined
      }
    | {
        /** Button text. */
        label: ReactNode
        /** Called when the button is clicked. */
        onClick: () => void
      }

  /** Props for {@link Info}. */
  type Props = {
    /** Optional trailing action rendered inline. */
    action?: Action | undefined
    /** Additional class names. */
    className?: string | undefined
    /** Message body. */
    message: ReactNode
    /** Visual variant. @default 'info' */
    type?: Type | undefined
  }
}
